<template>
  <!-- 제조사 실무자 통합 관리 화면 — SPEC-CHINA-MFR-STAFF-ACCOUNT-001 REQ-D-001 -->
  <!-- Section A: 실무자 계정 관리 (마스터 전용, REQ-D-002/005) -->
  <!-- Section B: 연관 기공소/병원 조회 전용 (REQ-D-003) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('mfrStaff_page_title') }}</span>
    </div>
    <!-- [축 안내 2026-08-06] 이 화면=우리 회사 직원 / 거래처 계정=각 관리 화면 — 세 화면이
         전부 '계정 관리'라 대상 축이 어디에도 안 적혀 있던 혼선의 직접 해소책 -->
    <a-alert type="info" show-icon style="margin-bottom: 12px;">
      <template #message>
        {{ $t('mfrStaff_axis_note') }}
        <router-link to="/hospital/list" style="margin-left: 8px;">{{ $t('mfrStaff_axis_link_hospital') }}</router-link>
        <router-link to="/dental-lab/list" style="margin-left: 8px;">{{ $t('mfrStaff_axis_link_dentalLab') }}</router-link>
      </template>
    </a-alert>

    <!-- STAFF 계정 접근 시 안내 (REQ-D-005) -->
    <a-alert
      v-if="!isMaster"
      type="warning"
      :message="$t('mfrStaff_staff_no_permission')"
      style="margin-bottom: 16px;"
      show-icon
    />

    <a-tabs
      v-model:activeKey="activeTab"
      type="card"
    >
      <!-- ====================================================================
           탭 A: 실무자 계정 관리 (마스터만 접근 가능 — REQ-D-002/005)
           ==================================================================== -->
      <a-tab-pane key="staff" :tab="$t('mfrStaff_tab_staff')">
        <!-- 마스터가 아닌 경우(STAFF 로그인) 섹션 전체 비노출 (REQ-D-005) -->
        <div v-if="!isMaster" style="text-align: center; padding: 48px 0; color: #999;">
          {{ $t('mfrStaff_staff_section_hidden') }}
        </div>

        <template v-else>
          <!-- 에러 표시 -->
          <a-alert
            v-if="listError"
            type="error"
            :message="listError"
            style="margin-bottom: 12px;"
            show-icon
            closable
            @close="listError = ''"
          />

          <!-- 필터 영역 (2026-07-23) — 검색어/회사타입/소속회사/계정유형/보유메뉴(멀티) -->
          <div class="staff-filter-bar">
            <a-input
              v-model:value="filters.keyword"
              :placeholder="$t('mfrStaff_filter_keyword')"
              allow-clear
              style="width: 180px;"
            />
            <a-select
              v-if="isOperator"
              v-model:value="filters.companyType"
              :placeholder="$t('mfrStaff_filter_company_type')"
              :options="companyTypeOptions"
              allow-clear
              style="width: 140px;"
            />
            <a-select
              v-if="isOperator"
              v-model:value="filters.companyName"
              :placeholder="$t('mfrStaff_filter_company')"
              :options="companyNameOptions"
              allow-clear
              show-search
              style="width: 180px;"
            />
            <a-select
              v-model:value="filters.accountType"
              :placeholder="$t('mfrStaff_filter_account_type')"
              :options="[
                { value: 'ADMIN', label: $t('mfrStaff_acc_type_master') },
                { value: 'STAFF', label: $t('mfrStaff_acc_type_staff') },
              ]"
              allow-clear
              style="width: 130px;"
            />
            <a-select
              v-model:value="filters.menuIds"
              mode="multiple"
              :placeholder="$t('mfrStaff_filter_menus')"
              :options="menuOptions"
              option-filter-prop="label"
              allow-clear
              :max-tag-count="2"
              style="min-width: 260px; flex: 1;"
            />
            <a-button @click="resetFilters">{{ $t('mfrStaff_filter_reset') }}</a-button>
            <span style="font-size: 12px; color: #999; white-space: nowrap;">
              {{ filteredAccounts.length }} / {{ accounts.length }}
            </span>
          </div>

          <!-- 실무자 계정 목록 테이블 -->
          <a-spin :spinning="loadingAccounts">
            <!-- 데스크탑: a-table -->
            <a-table
              v-if="!isMobileView"
              :data-source="filteredAccounts"
              :columns="accountColumns"
              :pagination="false"
              row-key="_id"
              size="small"
              style="margin-bottom: 16px;"
            >
              <template #title>
                <LockPolicyHint />
              </template>
              <template #bodyCell="{ column, record }">
                <!-- 운영자 전체 현황: 소속 회사 = 타입 태그 + 이름, 플랫폼(信徕安) 강조 -->
                <template v-if="column.key === 'companyName'">
                  <a-tag :color="companyTypeColor(record.companyType)">
                    {{ companyTypeLabel(record.companyType) }}
                  </a-tag>
                  <span :style="record.companyType === 'XINLAIAN' ? 'font-weight: 600;' : ''">
                    {{ record.companyName }}
                  </span>
                </template>

                <!-- 첫 로그인 여부 (REQ-D-006) -->
                <template v-if="column.key === 'passwordChangeRequired'">
                  <a-tag :color="record.passwordChangeRequired ? 'orange' : 'green'">
                    {{ record.passwordChangeRequired
                      ? $t('mfrStaff_acc_firstLogin_yes')
                      : $t('mfrStaff_acc_firstLogin_no') }}
                  </a-tag>
                </template>

                <!-- 역할 표시 (MANUFACTURER / STAFF 구분용) -->
                <template v-if="column.key === 'accountType'">
                  <a-tag :color="record.accountType === 'STAFF' ? 'blue' : 'purple'">
                    {{ record.accountType === 'STAFF'
                      ? $t('mfrStaff_acc_type_staff')
                      : $t('mfrStaff_acc_type_master') }}
                  </a-tag>
                  <!-- F5: 잠김 상태 — lockedUntil 미래면 잠김 + 남은 분 표시 -->
                  <a-tooltip v-if="isLocked(record)" :title="$t('acc_lock_failed_count', { n: record.failedLoginCount || 0 })">
                    <a-tag color="volcano">{{ $t('acc_locked_tag', { n: lockRemainingMinutes(record) }) }}</a-tag>
                  </a-tooltip>
                </template>

                <!-- 보유 메뉴 (2026-07-23) — 개수 태그 + 팝오버 목록 -->
                <template v-if="column.key === 'menus'">
                  <a-popover v-if="(record.menus || []).length > 0" placement="left">
                    <template #content>
                      <div style="max-height: 280px; overflow-y: auto; max-width: 320px;">
                        <div v-for="m in record.menus" :key="m.menuId" style="font-size: 12px; line-height: 1.9;">
                          <span style="color: #999;">{{ menuCategoryDisplay(m) }}</span> · {{ menuNameDisplay(m) }}
                        </div>
                      </div>
                    </template>
                    <a-tag color="blue" style="cursor: pointer;">{{ record.menus.length }}</a-tag>
                  </a-popover>
                  <span v-else style="color: #ccc;">0</span>
                </template>

                <!-- F2: 마지막 접속 정보 -->
                <template v-if="column.key === 'lastLogin'">
                  <span style="font-size: 12px; color: #666; white-space: nowrap;">
                    {{ formatLastLogin(record) }}
                  </span>
                </template>

                <!-- SPEC-CHINA-CAP-COST-001: 비용 열람 스위치. 저장이 deny-list라 스위치는 반전 표시다. -->
                <template v-if="column.key === 'costVisible'">
                  <a-switch
                    :checked="canSeeCost(record)"
                    :loading="capsSavingId === record._id"
                    size="small"
                    @change="(checked) => onToggleCostVisible(record, checked)"
                  />
                </template>

                <!-- SPEC-CHINA-CAP-ISSUE-001: 제작 스위치. 저장이 deny-list라 스위치는 반전 표시다. -->
                <template v-if="column.key === 'issueAllowed'">
                  <a-switch
                    :checked="canCap(record, 'certCard.issue')"
                    :loading="capsSavingId === record._id"
                    size="small"
                    @change="(checked) => onToggleCap(record, 'certCard.issue', checked)"
                  />
                </template>

                <!-- 행 액션 -->
                <template v-if="column.key === 'actions'">
                  <a-space>
                    <!-- 메뉴편집 (MenuModal 재사용 — REQ-D-002) -->
                    <a-button size="small" @click="openMenuModal(record)">
                      {{ $t('mfrStaff_acc_btn_menu') }}
                    </a-button>
                    <!-- 이름수정 (REQ-D-002) -->
                    <a-button size="small" @click="openUpdateModal(record)">
                      {{ $t('mfrStaff_acc_btn_edit') }}
                    </a-button>
                    <!-- 비번초기화 (REQ-D-002) — 자기 자신 비활성 (REQ-G-002) -->
                    <a-button
                      size="small"
                      danger
                      :disabled="isSelf(record)"
                      :title="isSelf(record) ? $t('mfrStaff_acc_self_action_blocked') : undefined"
                      @click="onResetPassword(record)"
                    >
                      {{ $t('mfrStaff_acc_btn_resetPwd') }}
                    </a-button>
                    <!-- F3/F4: 강제 로그아웃 — 자기 자신 비활성 (REQ-G-001) -->
                    <a-button
                      size="small"
                      :disabled="isSelf(record)"
                      :title="isSelf(record) ? $t('mfrStaff_acc_self_action_blocked') : undefined"
                      @click="onForceLogout(record)"
                    >
                      {{ $t('acc_btn_force_logout') }}
                    </a-button>
                    <!-- F5: 로그인 잠금 해제 — 세션·비번 무변경. self 도 허용(무해) -->
                    <a-button size="small" @click="onUnlock(record)">
                      {{ $t('acc_btn_unlock') }}
                    </a-button>
                    <!-- [패리티 2026-08-06] 중지/복구·삭제 — 거래처 계정모달에는 있는데 자기
                         직원에는 없던 역전 해소. BE 는 P1 때 기개방(status/DELETE), 자기계정은
                         BE cannotModifySelfAccount + FE isSelf 이중 차단. -->
                    <a-button
                      size="small"
                      :disabled="isSelf(record)"
                      :title="isSelf(record) ? $t('mfrStaff_acc_self_action_blocked') : undefined"
                      @click="onToggleActive(record)"
                    >
                      {{ record.useYn === 'N' ? $t('mfrStaff_acc_btn_restore') : $t('mfrStaff_acc_btn_suspend') }}
                    </a-button>
                    <a-button
                      size="small"
                      danger
                      :disabled="isSelf(record)"
                      :title="isSelf(record) ? $t('mfrStaff_acc_self_action_blocked') : undefined"
                      @click="onDeleteAccount(record)"
                    >
                      {{ $t('mfrStaff_acc_btn_delete') }}
                    </a-button>
                  </a-space>
                </template>
              </template>
            </a-table>

            <!-- 모바일: 카드뷰 -->
            <div v-else class="mobile-card-list" style="margin-bottom: 16px;">
              <a-empty v-if="filteredAccounts.length === 0" />
              <div v-for="record in filteredAccounts" :key="record._id" class="mobile-company-card">
                <!-- 운영자 전체 현황: 소속 회사 (REQ-A-007 개정) -->
                <div v-if="isOperator" class="card-field">
                  <span class="card-label">{{ $t('mfrStaff_acc_col_company') }}:</span>
                  <a-tag :color="companyTypeColor(record.companyType)">
                    {{ companyTypeLabel(record.companyType) }}
                  </a-tag>
                  <span :style="record.companyType === 'XINLAIAN' ? 'font-weight: 600;' : ''">
                    {{ record.companyName }}
                  </span>
                </div>
                <div class="card-field">
                  <span class="card-label">{{ $t('mfrStaff_acc_col_username') }}:</span>
                  <span>{{ record.username }}</span>
                </div>
                <div class="card-field">
                  <span class="card-label">{{ $t('mfrStaff_acc_col_name') }}:</span>
                  <span>{{ record.name }}</span>
                </div>
                <div class="card-field">
                  <span class="card-label">{{ $t('mfrStaff_acc_col_accountType') }}:</span>
                  <a-tag :color="record.accountType === 'STAFF' ? 'blue' : 'purple'">
                    {{ record.accountType === 'STAFF' ? $t('mfrStaff_acc_type_staff') : $t('mfrStaff_acc_type_master') }}
                  </a-tag>
                </div>
                <div class="card-field">
                  <span class="card-label">{{ $t('mfrStaff_acc_col_passwordChangeRequired') }}:</span>
                  <a-tag :color="record.passwordChangeRequired ? 'orange' : 'green'">
                    {{ record.passwordChangeRequired ? $t('mfrStaff_acc_firstLogin_yes') : $t('mfrStaff_acc_firstLogin_no') }}
                  </a-tag>
                </div>
                <!-- F2: 마지막 접속 (모바일 카드) -->
                <div class="card-field">
                  <span class="card-label">{{ $t('acc_col_last_login') }}:</span>
                  <span style="font-size: 12px; color: #666;">{{ formatLastLogin(record) }}</span>
                </div>
                <!-- F5: 잠김 상태 (모바일) -->
                <div v-if="isLocked(record)">
                  <a-tag color="volcano">{{ $t('acc_locked_tag', { n: lockRemainingMinutes(record) }) }}</a-tag>
                </div>
                <div class="card-actions">
                  <a-button size="small" @click="openMenuModal(record)">{{ $t('mfrStaff_acc_btn_menu') }}</a-button>
                  <a-button size="small" @click="openUpdateModal(record)">{{ $t('mfrStaff_acc_btn_edit') }}</a-button>
                  <a-button size="small" danger :disabled="isSelf(record)" @click="onResetPassword(record)">{{ $t('mfrStaff_acc_btn_resetPwd') }}</a-button>
                  <!-- F3/F4: 강제 로그아웃 (모바일) — 자기 자신 비활성 (REQ-G-001) -->
                  <a-button size="small" :disabled="isSelf(record)" @click="onForceLogout(record)">{{ $t('acc_btn_force_logout') }}</a-button>
                  <!-- F5: 로그인 잠금 해제 (모바일) -->
                  <a-button size="small" @click="onUnlock(record)">{{ $t('acc_btn_unlock') }}</a-button>
                  <a-button size="small" :disabled="isSelf(record)" @click="onToggleActive(record)">{{ record.useYn === 'N' ? $t('mfrStaff_acc_btn_restore') : $t('mfrStaff_acc_btn_suspend') }}</a-button>
                  <a-button size="small" danger :disabled="isSelf(record)" @click="onDeleteAccount(record)">{{ $t('mfrStaff_acc_btn_delete') }}</a-button>
                </div>
              </div>
            </div>
          </a-spin>

          <!-- 계정 추가 섹션 (REQ-D-002) — 운영자 전체 현황 모드에서는 비노출(생성은 회사 지정 필요) -->
          <div v-if="!isOperator" style="border-top: 1px solid #f0f0f0; padding-top: 12px;">
            <div style="font-weight: 600; margin-bottom: 4px;">{{ $t('mfrStaff_acc_create_title') }}</div>
            <!-- 초기 비밀번호=아이디 사전 안내 (기존: 생성 성공 후에만 표시) -->
            <div style="font-size: 12px; color: #999; margin-bottom: 10px;">{{ $t('mfrStaff_acc_create_hint') }}</div>

            <a-form layout="inline" :model="createForm" ref="createFormRef" @finish="onCreateStaff">
              <!-- ID(username) -->
              <a-form-item
                :label="$t('mfrStaff_acc_username_label')"
                name="username"
                :rules="[
                  { required: true, message: $t('mfrStaff_acc_username_required') },
                  { pattern: /^[a-zA-Z0-9._-]{4,30}$/, message: $t('mfrStaff_acc_username_pattern') },
                ]"
              >
                <a-input
                  v-model:value="createForm.username"
                  :placeholder="$t('mfrStaff_acc_username_placeholder')"
                  style="width: 200px;"
                />
              </a-form-item>

              <!-- 이름(name) -->
              <a-form-item
                :label="$t('mfrStaff_acc_name_label')"
                name="name"
                :rules="[
                  { required: true, message: $t('mfrStaff_acc_name_required') },
                  { max: 30, message: $t('mfrStaff_acc_name_maxlen') },
                ]"
              >
                <a-input
                  v-model:value="createForm.name"
                  :placeholder="$t('mfrStaff_acc_name_placeholder')"
                  style="width: 200px;"
                />
              </a-form-item>

              <a-form-item>
                <!-- html-type=submit: a-form rules 인라인 검증을 거쳐 @finish로 제출 -->
                <a-button
                  type="primary"
                  html-type="submit"
                  :loading="creatingAccount"
                >
                  {{ $t('mfrStaff_acc_btn_add') }}
                </a-button>
              </a-form-item>
            </a-form>

            <!-- 계정 추가 에러 (409 중복 포함) -->
            <a-alert
              v-if="createError"
              type="error"
              :message="createError"
              style="margin-top: 8px;"
              show-icon
              closable
              @close="createError = ''"
            />

            <!-- 계정 추가 성공 안내 (초기 비밀번호 = 아이디, 첫 로그인 변경 강제) -->
            <a-alert
              v-if="createSuccess"
              type="success"
              :message="createSuccess"
              style="margin-top: 8px;"
              show-icon
              closable
              @close="createSuccess = ''"
            />
          </div>

          <!-- 이름 수정 모달 (REQ-D-002: name만 허용) — Bug3 fix: ant-design-vue 4 표준 footer + 패딩 -->
          <a-modal
            :open="updateModalOpen"
            :title="$t('mfrStaff_acc_update_modal_title')"
            width="400px"
            :centered="true"
            :destroy-on-close="true"
            :confirm-loading="updatingAccount"
            :ok-text="$t('mfrStaff_acc_btn_save')"
            :cancel-text="$t('mfrStaff_acc_btn_cancel')"
            @ok="onUpdateAccount"
            @cancel="closeUpdateModal"
          >
            <div style="padding: 20px 0;">
              <a-form layout="vertical">
                <a-form-item :label="$t('mfrStaff_acc_name_label')">
                  <a-input
                    v-model:value="editName"
                    :placeholder="$t('mfrStaff_acc_name_placeholder')"
                  />
                </a-form-item>
              </a-form>
              <a-alert
                v-if="updateError"
                type="error"
                :message="updateError"
                style="margin-top: 8px;"
                show-icon
              />
            </div>
          </a-modal>

          <!-- MenuModal 재사용 (기존 패턴 그대로 — REQ-D-002) -->
          <!-- Bug1 fix: MenuModal은 'closeModal'을 emit하므로 kebab-case @close-modal로 수신 -->
          <MenuModal
            :show-modal="menuModalOpen"
            :username="menuModalUsername"
            :target-role="menuModalRole"
            :copy-source-accounts="accounts"
            @close-modal="closeMenuModal"
          />
        </template>
      </a-tab-pane>

      <!-- ====================================================================
           탭 B: 연관 기공소/병원 조회 전용 (REQ-D-003)
           MANUFACTURER role은 write 버튼 전체 숨김 (readOnly=true)
           운영자(XINLAIAN)는 기존 동작 그대로 유지 (회귀 없음)
           ==================================================================== -->
      <!-- [2026-08-06 재구성] 이중 탭('연관' → 병원/기공소 서브탭)을 평탄한 최상위 탭으로.
           readOnly 는 유지하되(회사 생성·수정·삭제는 병원/기공소 관리 화면 소관)
           accounts-enabled 로 계정 기능은 살린다 — '계정 관리' 화면이므로 여기서
           거래처 계정의 조회·생성·비번초기화·중지·삭제가 전부 가능해야 한다. -->
      <a-tab-pane key="hospital" :tab="$t('mfrStaff_tab_hospital')">
        <CompanyList
          type="HOSPITAL"
          :read-only="true"
          :accounts-enabled="true"
        />
      </a-tab-pane>

      <a-tab-pane key="dentalLab" :tab="$t('mfrStaff_tab_dental_lab')">
        <CompanyList
          type="DENTAL_LABORATORY"
          :read-only="true"
          :accounts-enabled="true"
        />
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
// @MX:ANCHOR: [AUTO] MfrStaffManage.vue — 제조사 실무자 통합 관리 화면 진입점
// @MX:REASON: [AUTO] router.js에서 라우트 등록 + CompanyList readOnly 조합 + manufacturer/accounts 4 API 경유. SPEC-CHINA-MFR-STAFF-ACCOUNT-001 REQ-D-001~006 구현.
// @MX:SPEC: SPEC-CHINA-MFR-STAFF-ACCOUNT-001

import CompanyList from '@/views/CompanyList/CompanyList.vue';
import MenuModal from '@/views/ManuAccManage/components/MenuModal.vue';
import LockPolicyHint from '@/components/LockPolicyHint.vue';
import {
  listManufacturerAccounts,
  createStaffAccount,
  updateManufacturerAccount,
  resetManufacturerAccountPassword,
  forceLogoutManufacturerAccount,
  unlockManufacturerAccount,
  setManufacturerAccountStatus,
  deleteManufacturerAccount,
  setManufacturerAccountCapabilities,
  extractApiError,
} from '@/apis/chinaCompany';
import { jwtAccountType, jwtUsername, isOperator, twoBtnDialog } from '@/utils/utils';
import dayjs from 'dayjs';

export default {
  name: 'MfrStaffManage',

  components: {
    CompanyList,
    MenuModal,
    LockPolicyHint,
  },

  data() {
    return {
      isMobileView: typeof window !== 'undefined' && window.innerWidth <= 767,
      // 현재 활성 탭
      activeTab: 'staff',

      // 실무자 목록
      accounts: [],
      loadingAccounts: false,
      listError: '',
      // SPEC-CHINA-CAP-COST-001: 비용 열람 토글 저장중 계정 _id
      capsSavingId: null,

      // 계정 추가
      createForm: { username: '', name: '' },
      creatingAccount: false,
      createError: '',
      createSuccess: '',

      // 이름 수정 모달
      updateModalOpen: false,
      editTarget: null,
      editName: '',
      updatingAccount: false,
      updateError: '',

      // MenuModal
      menuModalOpen: false,
      menuModalUsername: '',
      // 편집 대상 계정의 role — 메뉴 카탈로그 피커 필터에 사용(2026-07-30)
      menuModalRole: '',

      // 필터 영역 (2026-07-23) — 메뉴는 멀티선택(선택 메뉴 모두 보유한 계정만 표시)
      filters: {
        keyword: '',
        companyType: undefined,
        companyName: undefined,
        accountType: undefined,
        menuIds: [],
      },
    };
  },

  computed: {
    /**
     * 마스터 계정 여부 판단 — REQ-D-005
     * accountType !== 'STAFF' 이면 마스터(ADMIN 또는 미설정).
     * JWT localStorage 직접 디코딩 (auth store에 accountType 미저장).
     */
    isMaster() {
      return jwtAccountType() !== 'STAFF';
    },

    /**
     * 플랫폼 운영자 여부 — REQ-A-007 개정(2026-07-23).
     * 운영자는 companyId 미지정 전체 계정 현황 조회(소속회사 컬럼 표시).
     * 운영자 판정은 isOperator() 단일 지점을 쓴다(utils.js). 리터럴 비교 금지.
     */
    isOperator() {
      return isOperator();
    },

    /**
     * 필터 적용 결과 — 모든 조건 AND.
     * 메뉴 필터는 선택한 메뉴를 "모두" 보유한 계정만 통과(멀티선택 AND).
     */
    filteredAccounts() {
      const kw = this.filters.keyword.trim().toLowerCase();
      return this.accounts.filter((a) => {
        if (kw) {
          const hay = `${a.username || ''} ${a.name || ''}`.toLowerCase();
          if (!hay.includes(kw)) return false;
        }
        if (this.filters.companyType && a.companyType !== this.filters.companyType) return false;
        if (this.filters.companyName && a.companyName !== this.filters.companyName) return false;
        if (this.filters.accountType) {
          const type = a.accountType === 'STAFF' ? 'STAFF' : 'ADMIN';
          if (type !== this.filters.accountType) return false;
        }
        if (this.filters.menuIds.length > 0) {
          const owned = new Set((a.menus || []).map((m) => m.menuId));
          if (!this.filters.menuIds.every((id) => owned.has(id))) return false;
        }
        return true;
      });
    },

    /** 회사 타입 옵션 — 데이터에 존재하는 타입만 노출 */
    companyTypeOptions() {
      const types = [...new Set(this.accounts.map((a) => a.companyType).filter(Boolean))];
      return types.map((t) => ({ value: t, label: this.companyTypeLabel(t) }));
    },

    /** 소속 회사 옵션 — 데이터에 존재하는 회사만 노출 */
    companyNameOptions() {
      const names = [...new Set(this.accounts.map((a) => a.companyName).filter(Boolean))];
      return names.map((n) => ({ value: n, label: n }));
    },

    /**
     * 메뉴 멀티선택 옵션 — 전 계정 보유 메뉴의 합집합.
     * 라벨은 "카테고리 · 메뉴명" (현재 로케일), 카테고리→메뉴명 정렬.
     */
    menuOptions() {
      const byId = new Map();
      for (const a of this.accounts) {
        for (const m of (a.menus || [])) {
          if (!byId.has(m.menuId)) byId.set(m.menuId, m);
        }
      }
      return [...byId.values()]
        .map((m) => ({
          value: m.menuId,
          label: `${this.menuCategoryDisplay(m)} · ${this.menuNameDisplay(m)}`,
        }))
        .sort((x, y) => x.label.localeCompare(y.label));
    },

    /**
     * 로그인 계정 username — 자기 행 위험 액션 비활성용 (REQ-G-001/002 FE 미러).
     * SPEC-CHINA-MFR-STAFF-GUARD-001
     */
    myUsername() {
      return jwtUsername();
    },

    accountColumns() {
      return [
        // 운영자 전체 현황: 소속 회사 컬럼 (REQ-A-007 개정)
        ...(this.isOperator
          ? [{ title: this.$t('mfrStaff_acc_col_company'), dataIndex: 'companyName', key: 'companyName' }]
          : []),
        { title: this.$t('mfrStaff_acc_col_username'), dataIndex: 'username', key: 'username' },
        { title: this.$t('mfrStaff_acc_col_name'), dataIndex: 'name', key: 'name' },
        { title: this.$t('mfrStaff_acc_col_accountType'), key: 'accountType' },
        { title: this.$t('mfrStaff_acc_col_passwordChangeRequired'), key: 'passwordChangeRequired' },
        // 보유 메뉴 (2026-07-23) — 개수 + 팝오버 목록
        { title: this.$t('mfrStaff_acc_col_menus'), key: 'menus', width: 100, align: 'center' },
        // F2: 마지막 접속 정보
        { title: this.$t('acc_col_last_login'), key: 'lastLogin', width: 200 },
        // SPEC-CHINA-CAP-COST-001: 비용 열람 권한 토글 (마스터가 계정별 제어)
        { title: this.$t('mfrStaff_acc_col_costVisible'), key: 'costVisible', width: 110, align: 'center' },
        // SPEC-CHINA-CAP-ISSUE-001: 인증서 제작 실행 권한 토글
        { title: this.$t('mfrStaff_acc_col_issueAllowed'), key: 'issueAllowed', width: 110, align: 'center' },
        { title: this.$t('mfrStaff_acc_col_actions'), key: 'actions', width: 280 },
      ];
    },

    // F2: 마지막 접속 포맷 헬퍼
    formatLastLogin() {
      return (record) => {
        if (!record.lastLoginAt) return '-';
        const date = dayjs(record.lastLoginAt).format('YYYY-MM-DD HH:mm');
        const parts = [date];
        if (record.lastLoginIp) parts.push(record.lastLoginIp);
        const browser = record.lastLoginBrowser || record.lastLoginOs;
        if (browser) parts.push(browser);
        return parts.join(' · ');
      };
    },
  },

  mounted() {
    // 마스터인 경우에만 실무자 목록 초기 로드
    if (this.isMaster) {
      this.fetchAccounts();
    }
    window.addEventListener('resize', this.onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    onResize() { this.isMobileView = window.innerWidth <= 767; },

    /**
     * 소속 회사 type 태그 색 — 운영자 전체 현황 뷰(REQ-A-007 개정).
     * XINLAIAN(플랫폼 마스터 信徕安)은 gold로 구분 강조.
     */
    companyTypeColor(type) {
      return {
        XINLAIAN: 'gold',
        MANUFACTURER: 'purple',
        DEALER: 'magenta',
        HOSPITAL: 'green',
        DENTAL_LABORATORY: 'cyan',
      }[type] || 'default';
    },

    /** 필터 초기화 */
    resetFilters() {
      this.filters = {
        keyword: '',
        companyType: undefined,
        companyName: undefined,
        accountType: undefined,
        menuIds: [],
      };
    },

    /** 메뉴명 로케일 표시 (ko/zh/fallback) */
    menuNameDisplay(m) {
      const locale = this.$i18n?.locale || 'ko';
      if (locale === 'zh') return m.nameZh || m.name || m.nameKo || m.menuId;
      if (locale === 'ko') return m.nameKo || m.name || m.nameZh || m.menuId;
      return m.name || m.nameKo || m.nameZh || m.menuId;
    },

    /** 메뉴 카테고리 로케일 표시 */
    menuCategoryDisplay(m) {
      const locale = this.$i18n?.locale || 'ko';
      if (locale === 'zh') return m.categoryZh || m.category || m.categoryKo || '-';
      if (locale === 'ko') return m.categoryKo || m.category || m.categoryZh || '-';
      return m.category || m.categoryKo || m.categoryZh || '-';
    },

    companyTypeLabel(type) {
      const key = {
        XINLAIAN: 'mfrStaff_company_type_platform',
        MANUFACTURER: 'mfrStaff_company_type_manufacturer',
        DEALER: 'mfrStaff_company_type_dealer',
        HOSPITAL: 'mfrStaff_company_type_hospital',
        DENTAL_LABORATORY: 'mfrStaff_company_type_dental_lab',
      }[type];
      return key ? this.$t(key) : (type || '-');
    },

    // 자기 자신 행 여부 — 비번초기화/강제 로그아웃 비활성 (BE REQ-G-001/002와 동일 기준)
    // SPEC-CHINA-CAP-ISSUE-001: capability 보유 여부/토글 — cap 파라미터화 공용 헬퍼
    canCap(record, cap) {
      const denied = Array.isArray(record.deniedCaps) ? record.deniedCaps : [];
      return !denied.includes(cap);
    },

    async onToggleCap(record, cap, checked) {
      const nextDenied = checked
        ? (record.deniedCaps || []).filter((c) => c !== cap)
        : Array.from(new Set([...(record.deniedCaps || []), cap]));
      this.capsSavingId = record._id;
      try {
        await setManufacturerAccountCapabilities(record._id, nextDenied);
        record.deniedCaps = nextDenied;
        this.$message.success(this.$t('mfrStaff_acc_cap_saved'));
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('mfrStaff_error_403')
            : apiErr.message || this.$t('mfrStaff_error_save_failed'),
        );
      } finally {
        this.capsSavingId = null;
      }
    },

    // SPEC-CHINA-CAP-COST-001: 저장된 deniedCaps 기준 스위치 상태(뺄셈 목록의 반전).
    // 실제 인가 판정이 아니라 '마스터가 무엇을 차감해 뒀는가'의 표시다.
    canSeeCost(record) {
      const denied = Array.isArray(record.deniedCaps) ? record.deniedCaps : [];
      return !denied.includes('certCard.cost');
    },

    // 비용 열람 토글 — 마스터가 계정별 제어. tokenVersion bump로 대상 재로그인 후 반영.
    async onToggleCostVisible(record, checked) {
      const nextDenied = checked
        ? (record.deniedCaps || []).filter((c) => c !== 'certCard.cost')
        : Array.from(new Set([...(record.deniedCaps || []), 'certCard.cost']));
      this.capsSavingId = record._id;
      try {
        await setManufacturerAccountCapabilities(record._id, nextDenied);
        // 낙관적 로컬 반영 (재조회 없이 스위치 상태 유지)
        record.deniedCaps = nextDenied;
        this.$message.success(this.$t('mfrStaff_acc_costVisible_saved'));
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('mfrStaff_error_403')
            : apiErr.message || this.$t('mfrStaff_error_save_failed'),
        );
      } finally {
        this.capsSavingId = null;
      }
    },

    isSelf(record) {
      return !!this.myUsername && record.username === this.myUsername;
    },

    // 실무자 목록 조회 — GET /china/manufacturer/accounts
    async fetchAccounts() {
      this.loadingAccounts = true;
      this.listError = '';
      try {
        const res = await listManufacturerAccounts();
        this.accounts = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        const apiErr = extractApiError(err);
        this.listError = apiErr.status === 403
          ? this.$t('mfrStaff_error_403')
          : apiErr.message || this.$t('mfrStaff_error_fetch_failed');
      } finally {
        this.loadingAccounts = false;
      }
    },

    // 실무자 계정 추가 — POST /china/manufacturer/accounts
    // @MX:WARN: [AUTO] 409(중복 ID) 에러 UX 필수 — AC-D-002 must-pass
    // @MX:REASON: [AUTO] username 전역 중복 시 409, 미처리 시 화면 멈춤. extractApiError로 상태 분기 필수.
    async onCreateStaff() {
      const username = this.createForm.username.trim();
      const name = this.createForm.name.trim();
      if (!username) {
        this.createError = this.$t('mfrStaff_acc_username_required');
        return;
      }
      if (!name) {
        this.createError = this.$t('mfrStaff_acc_name_required');
        return;
      }
      this.creatingAccount = true;
      this.createError = '';
      this.createSuccess = '';
      try {
        await createStaffAccount({ username, name });
        // 성공 피드백: 초기 비밀번호 = 아이디 안내 (REQ-D-006, 첫 로그인 변경 강제)
        const successMsg = this.$t('mfrStaff_acc_create_success', { username });
        this.createSuccess = successMsg;
        this.$message.success(successMsg);
        // 입력 초기화 + validation 워닝 해제
        this.createForm.username = '';
        this.createForm.name = '';
        if (this.$refs.createFormRef) {
          this.$refs.createFormRef.resetFields();
        }
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        if (apiErr.status === 409) {
          this.createError = this.$t('mfrStaff_acc_error_dup_username');
        } else if (apiErr.status === 403) {
          this.createError = this.$t('mfrStaff_error_403');
        } else {
          this.createError = apiErr.message || this.$t('mfrStaff_error_save_failed');
        }
      } finally {
        this.creatingAccount = false;
      }
    },

    // 이름 수정 모달 열기
    openUpdateModal(record) {
      this.editTarget = record;
      this.editName = record.name || '';
      this.updateError = '';
      this.updateModalOpen = true;
    },

    closeUpdateModal() {
      this.updateModalOpen = false;
      this.editTarget = null;
      this.editName = '';
    },

    // 이름 수정 제출 — PATCH /china/manufacturer/accounts/:accountId
    async onUpdateAccount() {
      if (!this.editTarget) return;
      this.updatingAccount = true;
      this.updateError = '';
      try {
        await updateManufacturerAccount(this.editTarget._id, {
          name: this.editName,
        });
        this.closeUpdateModal();
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.updateError = apiErr.status === 403
          ? this.$t('mfrStaff_error_403')
          : apiErr.message || this.$t('mfrStaff_error_save_failed');
      } finally {
        this.updatingAccount = false;
      }
    },

    // 비밀번호 초기화 — POST /china/manufacturer/accounts/:accountId/reset-password
    // BE가 username으로 재설정 + passwordChangeRequired=true → 첫 로그인 강제변경 게이트 활성 (REQ-D-006)
    async onResetPassword(record) {
      const confirmed = await twoBtnDialog(
        this.$t('mfrStaff_acc_resetPwd_confirm_title'),
        this.$t('mfrStaff_acc_resetPwd_confirm_text'),
        this.$t('mfrStaff_acc_btn_resetPwd'),
        this.$t('mfrStaff_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        await resetManufacturerAccountPassword(record._id);
        this.$message.success(this.$t('mfrStaff_acc_resetPwd_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('mfrStaff_error_403')
            : apiErr.message || this.$t('mfrStaff_error_save_failed'),
        );
      }
    },

    // F3/F4: 강제 로그아웃 — POST /china/manufacturer/accounts/:id/force-logout
    async onForceLogout(record) {
      const confirmed = await twoBtnDialog(
        this.$t('acc_force_logout_confirm_title'),
        this.$t('acc_force_logout_confirm_text'),
        this.$t('acc_btn_force_logout'),
        this.$t('mfrStaff_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        await forceLogoutManufacturerAccount(record._id);
        this.$message.success(this.$t('acc_force_logout_success'));
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('acc_force_logout_error'));
      }
    },

    // F5: 잠김 판정 — lockedUntil 이 미래인 동안만 잠김 (클라이언트 시계 기준)
    isLocked(record) {
      return !!record.lockedUntil && new Date(record.lockedUntil).getTime() > Date.now();
    },

    lockRemainingMinutes(record) {
      if (!this.isLocked(record)) return 0;
      return Math.ceil((new Date(record.lockedUntil).getTime() - Date.now()) / 60000);
    },

    // F5: 로그인 잠금 해제 — POST /china/manufacturer/accounts/:id/unlock (세션·비번 무변경)
    async onUnlock(record) {
      const confirmed = await twoBtnDialog(
        this.$t('acc_unlock_confirm_title'),
        this.$t('acc_unlock_confirm_text'),
        this.$t('acc_btn_unlock'),
        this.$t('mfrStaff_acc_btn_cancel'),
      );
      if (!confirmed) return;

      try {
        await unlockManufacturerAccount(record._id);
        this.$message.success(this.$t('acc_unlock_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('acc_unlock_error'));
      }
    },

    /**
     * [패리티 2026-08-06] 중지/복구. 중지된 계정은 로그인·refresh 가 모두 거부된다(BE M1).
     * 성공 후 목록 재조회 — useYn 토글이 서버 상태와 어긋나지 않게(낙관적 갱신 금지).
     */
    async onToggleActive(record) {
      const activating = record.useYn === 'N';
      const confirmed = await twoBtnDialog(
        activating ? this.$t('mfrStaff_acc_restore_confirm_title') : this.$t('mfrStaff_acc_suspend_confirm_title'),
        activating ? this.$t('mfrStaff_acc_restore_confirm_text') : this.$t('mfrStaff_acc_suspend_confirm_text'),
        activating ? this.$t('mfrStaff_acc_btn_restore') : this.$t('mfrStaff_acc_btn_suspend'),
        this.$t('mfrStaff_acc_btn_cancel'),
      );
      if (!confirmed) return;
      try {
        await setManufacturerAccountStatus(record._id, activating);
        this.$message.success(activating ? this.$t('mfrStaff_acc_restore_success') : this.$t('mfrStaff_acc_suspend_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('mfrStaff_acc_status_error'));
      }
    },

    /** [패리티 2026-08-06] 소프트 삭제 — BE 가 자기계정을 cannotModifySelfAccount 로 차단 */
    async onDeleteAccount(record) {
      const confirmed = await twoBtnDialog(
        this.$t('mfrStaff_acc_delete_confirm_title'),
        this.$t('mfrStaff_acc_delete_confirm_text', { name: record.username }),
        this.$t('mfrStaff_acc_btn_delete'),
        this.$t('mfrStaff_acc_btn_cancel'),
      );
      if (!confirmed) return;
      try {
        await deleteManufacturerAccount(record._id);
        this.$message.success(this.$t('mfrStaff_acc_delete_success'));
        await this.fetchAccounts();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(apiErr.message || this.$t('mfrStaff_acc_delete_error'));
      }
    },

    // MenuModal 제어
    openMenuModal(record) {
      this.menuModalUsername = record.username;
      this.menuModalRole = record.role || '';
      this.menuModalOpen = true;
    },

    closeMenuModal() {
      this.menuModalOpen = false;
      this.menuModalUsername = '';
      this.menuModalRole = '';
    },
  },
};
</script>

<style scoped>
/* 모바일 카드뷰 */
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.mobile-company-card {
  border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff;
}
.card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: flex-start; }
.card-label { font-weight: 600; min-width: 110px; color: #666; flex-shrink: 0; }
.card-actions {
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px;
  border-top: 1px solid #f5f5f5; padding-top: 8px;
}
.card-actions :deep(.ant-btn) { flex: 1 1 auto; }

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
}

/* 필터 영역 (2026-07-23) */
.staff-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}
</style>
